import React, { useEffect, useMemo, useState } from 'react';
import ProjectTeamScheduleService from '../../services/ProjectTeamScheduleService';
import ProjectService from '../../services/ProjectService';
import MemberService from '../../services/MemberService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const ProjectTeamSchedule = () => {
    const [teamSchedules, setTeamSchedules] = useState([]);
    const [projects, setProjects] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterMember, setFilterMember] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        projectId: '',
        memberId: '',
        availableDate: '',
        availableEndDate: '',
        status: 'available'
    });
    const [bulkProjectId, setBulkProjectId] = useState('');
    const [bulkFormData, setBulkFormData] = useState([{
        memberId: '',
        availableDate: '',
        availableEndDate: '',
        status: 'available'
    }]);
    const filteredTeamSchedules = useMemo(() => teamSchedules.filter((schedule) => {
        if (!searchText) return true;
        return filterList([schedule], searchText, ['projectName', 'memberName', 'status']).length > 0;
    }), [teamSchedules, searchText]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterProject('');
        setFilterMember('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchDropdownData();
        fetchData();
    }, [currentPage, filterProject, filterMember, filterStatus]);

    const fetchDropdownData = async () => {
        try {
            const [projectsRes, membersRes] = await Promise.all([
                ProjectService.getAll(1, 100), // Get all projects for dropdown
                MemberService.getAll(1, 100) // Get all members
            ]);
            setProjects(projectsRes.data || []);
            setMembers(membersRes.data || []);
        } catch (err) {
            utility.showError('Failed to load dropdown data: ' + err.message);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await ProjectTeamScheduleService.getAll(currentPage, itemsPerPage, {
                projectId: filterProject,
                memberId: filterMember,
                status: filterStatus
            });
            setTeamSchedules(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (teamSchedule = null) => {
        if (teamSchedule) {
            setEditMode(true);
            setFormData({
                id: teamSchedule.id,
                projectId: teamSchedule.projectId,
                memberId: teamSchedule.memberId || '',
                availableDate: teamSchedule.availableDate ? teamSchedule.availableDate.split('T')[0] : '',
                availableEndDate: teamSchedule.availableEndDate ? teamSchedule.availableEndDate.split('T')[0] : '',
                status: teamSchedule.status
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                projectId: '',
                memberId: '',
                availableDate: '',
                availableEndDate: '',
                status: 'available'
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            id: '',
            projectId: '',
            memberId: '',
            availableDate: '',
            availableEndDate: '',
            status: 'available'
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await ProjectTeamScheduleService.update(formData.id, formData);
                utility.showSuccess('Project team schedule updated successfully');
            } else {
                await ProjectTeamScheduleService.create(formData);
                utility.showSuccess('Project team schedule created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };

    const handleBulkOpenModal = () => {
        setBulkProjectId('');
        setBulkFormData([{
            memberId: '',
            availableDate: '',
            availableEndDate: '',
            status: 'available'
        }]);
        setShowBulkModal(true);
    };

    const handleBulkCloseModal = () => {
        setShowBulkModal(false);
        setBulkProjectId('');
        setBulkFormData([{
            memberId: '',
            availableDate: '',
            availableEndDate: '',
            status: 'available'
        }]);
    };

    const handleBulkInputChange = (index, field, value) => {
        const updatedBulkData = [...bulkFormData];
        updatedBulkData[index][field] = value;
        setBulkFormData(updatedBulkData);
    };

    const handleAddBulkRow = () => {
        setBulkFormData([...bulkFormData, {
            projectId: '',
            memberId: '',
            availableDate: '',
            availableEndDate: '',
            status: 'available'
        }]);
    };

    const handleRemoveBulkRow = (index) => {
        if (bulkFormData.length > 1) {
            setBulkFormData(bulkFormData.filter((_, i) => i !== index));
        }
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const payload = bulkFormData.map(schedule => ({ ...schedule, projectId: bulkProjectId }));
            await ProjectTeamScheduleService.createBulk(payload);
            utility.showSuccess(`${bulkFormData.length} team schedules added successfully`);
            handleBulkCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0 me-2" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add Team Schedule
                    </button>
                    <button className="btn bg-gradient-info mb-0" onClick={handleBulkOpenModal}>
                        <i className="material-symbols-rounded text-sm">playlist_add</i>&nbsp;&nbsp;Add Multiple
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Project Team Schedules</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-md-4">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search project team schedules..."
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label text-muted small">Project</label>
                                            <select className="form-select" value={filterProject} onChange={handleFilterChange(setFilterProject)}>
                                                <option value="">All Projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label text-muted small">Member</label>
                                            <select className="form-select" value={filterMember} onChange={handleFilterChange(setFilterMember)}>
                                                <option value="">All Members</option>
                                                {members.map((member) => (
                                                    <option key={member.id} value={member.id}>{member.memberName || member.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="available">Available</option>
                                                <option value="unavailable">Unavailable</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-4">
                                            <label className="form-label text-muted small">&nbsp;</label>
                                            <button type="button" className="mt-1 btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Project</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Member</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Available Date</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Available End Date</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTeamSchedules.length === 0 ? (
                                                <tr><td colSpan="6" className="text-center py-4">No team schedules found.</td></tr>
                                            ) : (
                                                filteredTeamSchedules.map((schedule) => (
                                                    <tr key={schedule.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{schedule.projectName || 'Unknown'}</h6>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{schedule.memberName || schedule.teamMemberName || 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{schedule.availableDate ? new Date(schedule.availableDate).toLocaleDateString() : 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{schedule.availableEndDate ? new Date(schedule.availableEndDate).toLocaleDateString() : 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${schedule.status === 'available' ? 'success' : 'secondary'}`}
                                                            >
                                                                {schedule.status}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(schedule)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    <ReactPaginate
                                                        previousLabel="<"
                                                        nextLabel=">"
                                                        breakLabel="..."
                                                        pageCount={pageCount}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        onPageChange={handlePageClick}
                                                        forcePage={currentPage - 1}
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal */}
                {showModal && (
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title font-weight-normal">
                                        {editMode ? 'Edit Team Schedule' : 'Add Team Schedule'}
                                    </h5>
                                    <button type="button" className="btn-close text-dark" aria-label="Close" onClick={handleCloseModal} />
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body row">
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Project *</label>
                                                <select
                                                    name="projectId"
                                                    className="form-control"
                                                    value={formData.projectId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Project</option>
                                                    {projects.map(project => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Member *</label>
                                                <select
                                                    name="memberId"
                                                    className="form-control"
                                                    value={formData.memberId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Member</option>
                                                    {members.map(member => (
                                                        <option key={member.id} value={member.id}>
                                                            {member.memberName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Available Date *</label>
                                                <input
                                                    type="date"
                                                    name="availableDate"
                                                    className="form-control"
                                                    value={formData.availableDate}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Available End Date</label>
                                                <input
                                                    type="date"
                                                    name="availableEndDate"
                                                    className="form-control"
                                                    value={formData.availableEndDate}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Status</label>
                                                <select
                                                    name="status"
                                                    className="form-control"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="Available">Available</option>
                                                    <option value="Unavailable">Unavailable</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                        <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {editMode ? 'Updating...' : 'Saving...'}
                                                </>
                                            ) : (
                                                editMode ? 'Update' : 'Save'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Modal */}
                {showBulkModal && (
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title font-weight-normal">Add Multiple Team Schedules</h5>
                                    <button type="button" className="btn-close text-dark" aria-label="Close" onClick={handleBulkCloseModal} />
                                </div>
                                <form onSubmit={handleBulkSubmit}>
                                    <div className="modal-body">
                                        <div className="col-md-12 mb-3">
                                            <div className="input-group input-group-static">
                                                <label>Project *</label>
                                                <select
                                                    name="bulkProjectId"
                                                    className="form-control"
                                                    value={bulkProjectId}
                                                    onChange={(e) => setBulkProjectId(e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select Project</option>
                                                    {projects.map(project => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Member</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Available Date</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Available End Date</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Status</th>
                                                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bulkFormData.map((row, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.memberId}
                                                                    onChange={(e) => handleBulkInputChange(index, 'memberId', e.target.value)}
                                                                    required
                                                                >
                                                                    <option value="">Select</option>
                                                                    {members.map(member => (
                                                                        <option key={member.id} value={member.id}>
                                                                            {member.memberName || member.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.memberId}
                                                                    onChange={(e) => handleBulkInputChange(index, 'memberId', e.target.value)}
                                                                    required
                                                                >
                                                                    <option value="">Select</option>
                                                                    {members.map(member => (
                                                                        <option key={member.id} value={member.id}>
                                                                            {member.memberName || member.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={row.availableDate}
                                                                    onChange={(e) => handleBulkInputChange(index, 'availableDate', e.target.value)}
                                                                    required
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={row.availableEndDate}
                                                                    onChange={(e) => handleBulkInputChange(index, 'availableEndDate', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.status}
                                                                    onChange={(e) => handleBulkInputChange(index, 'status', e.target.value)}
                                                                >
                                                                    <option value="available">Available</option>
                                                                    <option value="unavailable">Unavailable</option>
                                                                </select>
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link text-danger mb-0 p-0"
                                                                    onClick={() => handleRemoveBulkRow(index)}
                                                                    disabled={bulkFormData.length === 1}
                                                                >
                                                                    <i className="material-symbols-rounded">delete</i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary mt-3"
                                            onClick={handleAddBulkRow}
                                        >
                                            <i className="material-symbols-rounded text-sm">add_circle</i>&nbsp;Add Row
                                        </button>
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button type="button" className="btn bg-gradient-secondary" onClick={handleBulkCloseModal} disabled={saving}>Cancel</button>
                                        <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save All'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectTeamSchedule;